import React, { FC } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { dateFormat } from '../../../util-helpers/common';

const css = require('./Vitals.module.scss');

dayjs.extend(utc);

interface IVital {
  fieldName: string;
  friendlyName: string;
  fieldType: string;
  value?: any;
  position: number;
}

interface IVitalsProps {
  vitals: IVital[];
  phaseName?: string;
}

interface IAgeYearsVital {
  birthDate: string;
  deathDate?: string;
  isDeceased?: boolean;
}

const vital = (key: string, value: string, name: string) => (
  <div key={key} className={css.vitalRow}>
    <div className={css.vitalName}>{`${name}:`}</div>
    <div className={css.vitalValueContainer}>
      <span className={css.vitalValue}>{value || '-'}</span>
    </div>
  </div>
);

const formatTime = (value: string, type: 'DateUtc' | 'DateTimeUtc' | 'ComboDate' | 'DateOnly', isUtc: boolean) => {
  if (!value) return '';
  // Keep legacy handling for V1 vitals that may still send these types.
  const hasUtcOffset = /Z$|[+-]\d{2}:\d{2}$/.test(value);
  const shouldUseUtc = isUtc || hasUtcOffset;

  if (type === 'DateTimeUtc' || type === 'ComboDate') {
    const fDateTime = dateFormat.DATE_AND_TIME;
    return shouldUseUtc ? dayjs.utc(value).format(fDateTime) : dayjs(value).format(fDateTime);
  }
  const fDate = dateFormat.DATE_ONLY;
  return shouldUseUtc ? dayjs.utc(value).format(fDate) : dayjs(value).format(fDate);
};

const formatAge = (value: string) => {
  const birthdate = dayjs(value);
  if (!birthdate.isValid()) {
    return value;
  }

  const now = dayjs();
  return now.diff(birthdate, 'year');
};

const yesNo = (input: boolean | string | unknown): string => {
  if (typeof input === 'boolean') {
    return input ? 'Yes' : 'No';
  }
  if (typeof input === 'string') {
    const str = input.toLowerCase();
    return (
      (['true', 'yes'].includes(str) && 'Yes')
      || (['false', 'no'].includes(str) && 'No')
      || ''
    );
  }

  return '';
};

const formatArgYears = (value: IAgeYearsVital) => {
  const birthdate = dayjs(value.birthDate);
  if (!birthdate.isValid()) {
    return value.birthDate;
  }

  const now = dayjs();
  const currentAge = now.diff(birthdate, 'year');

  const isDeceased = value.isDeceased === true;
  const hasDeathDate = !!value.deathDate;

  if (isDeceased && hasDeathDate) {
    const deathdate = dayjs(value.deathDate);
    if (deathdate.isValid()) {
      return deathdate.diff(birthdate, 'year');
    }
  }
  return currentAge;
};

const filteredVitals = ({
  fieldName, fieldType, friendlyName, value,
}: IVital) => {
  let newVal;
  let sl;

  if (fieldName === 'phase' || !fieldType) return null;

  switch (fieldType) {
    case 'MultiSelectList':
    case 'UrlList':
    case 'StringList':
      sl = typeof value === 'string' ? value.trim() : '';
      newVal = (sl.startsWith('[') && sl.endsWith(']')) ? sl.slice(1, -1).replace(/,/g, ' ') : sl;
      break;
    case 'DateOnly':
    case 'ComboDate':
      newVal = formatTime(value, fieldType, false);
      break;
    case 'DateUtc':
    case 'DateTimeUtc':
      newVal = formatTime(value, fieldType, true);
      break;
    case 'AgeYears':
      if (value && typeof value === 'object') {
        newVal = formatArgYears(value);
      } else {
        newVal = formatAge(value);
      }
      break;
    case 'Currency':
      newVal = value ? `$${value}` : value;
      break;
    case 'Percent':
      newVal = value ? `${value}%` : value;
      break;
    case 'Boolean':
      newVal = yesNo(value);
      break;
    default:
      newVal = value;
      break;
  }

  return vital(fieldName, newVal, friendlyName);
};

const Vitals: FC<IVitalsProps> = ({ vitals, phaseName }: IVitalsProps) => (
  <div className={css.vitalsContainer}>
    {phaseName && vital('phaseName', phaseName, 'Phase')}
    {!!vitals.length && vitals.map((v: IVital) => (filteredVitals(v)))}
  </div>
);
export default Vitals;
